import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Banner, Button, Card, Spinner } from "@magarac/ui";
import { isApiError } from "@magarac/shared/errors";
import { checkoutResponseSchema, publicPaymentSchema } from "@magarac/shared/schemas/payment";
import { api } from "../lib/api";
import { messageFor } from "../lib/errors";
import { formatMoney } from "../lib/format";

export const Route = createFileRoute("/pay/$token/")({
  validateSearch: (s: Record<string, unknown>): { paid?: boolean } => ({
    paid: s.paid === "1" || s.paid === 1 || s.paid === true ? true : undefined,
  }),
  component: PayPage,
});

/**
 * CRM-20, CRM-21, CRM-24: the customer's payment page (no sign-in). Every state says what is
 * going on and what to do: pay, wait a moment, already paid, link replaced, not found.
 */
function PayPage() {
  const { token } = Route.useParams();
  const { paid: justPaid } = Route.useSearch();
  const q = useQuery({
    queryKey: ["public-pay", token],
    queryFn: () => api.get(`/public/pay/${token}`, publicPaymentSchema),
    retry: false,
    // Just back from checkout: the payment lands a moment later (webhook), so keep checking.
    refetchInterval: (query) =>
      justPaid && query.state.data?.status === "open" && query.state.dataUpdateCount < 40
        ? 1500
        : false,
  });
  // Never a spinner forever: after 30 seconds say what is going on instead.
  const [slow, setSlow] = useState(false);
  useEffect(() => {
    if (!justPaid) return;
    const t = setTimeout(() => setSlow(true), 30_000);
    return () => clearTimeout(t);
  }, [justPaid]);
  const checkout = useMutation({
    mutationFn: () => api.post(`/public/pay/${token}/checkout`, {}, checkoutResponseSchema),
    onSuccess: ({ url }) => window.location.assign(url),
  });

  if (q.isLoading)
    return (
      <Shell>
        <Spinner />
      </Shell>
    );
  if (q.error) {
    const notFound = isApiError(q.error) && q.error.status === 404;
    return (
      <Shell>
        <Card>
          <h1 className="mb-2 text-xl font-semibold">
            {notFound ? "We could not find this payment link" : "This page did not load"}
          </h1>
          <p className="text-muted">
            {notFound
              ? "Check that the whole link from your text or email was opened. If it still does not work, contact the business that sent it."
              : messageFor(q.error)}
          </p>
          {!notFound ? (
            <Button className="mt-4" onClick={() => void q.refetch()}>
              Try again
            </Button>
          ) : null}
        </Card>
      </Shell>
    );
  }
  const p = q.data!;
  const call = p.businessPhone ? ` Call ${p.businessPhone}.` : "";

  return (
    <Shell>
      <p className="mb-1 text-sm text-muted">{p.businessName}</p>
      <Card>
        {p.status === "paid" ? (
          <>
            <h1 className="mb-2 text-xl font-semibold">Paid. Thank you!</h1>
            <p className="text-muted">
              {formatMoney(p.amountCents)} for{" "}
              {p.description.replace(/^(Deposit|Payment) for /, "")} has been received.
            </p>
          </>
        ) : p.status === "canceled" ? (
          <>
            <h1 className="mb-2 text-xl font-semibold">This link is no longer active</h1>
            <p className="text-muted">
              The amount changed or it was already settled another way, so nothing is due here.
              {call ? ` Questions?${call}` : ""}
            </p>
          </>
        ) : justPaid && slow ? (
          <>
            <h1 className="mb-2 text-xl font-semibold">Your payment is still being confirmed</h1>
            <p className="text-muted">
              If your card was charged, you do not need to pay again: {p.businessName} will see it
              shortly.{call ? ` Questions?${call}` : ""}
            </p>
            <Button className="mt-4" variant="secondary" onClick={() => void q.refetch()}>
              Check again
            </Button>
          </>
        ) : justPaid ? (
          <>
            <h1 className="mb-2 text-xl font-semibold">Confirming your payment…</h1>
            <Spinner />
            <p className="mt-2 text-sm text-muted">
              This usually takes a few seconds. You do not need to pay again.
            </p>
          </>
        ) : (
          <>
            <h1 className="mb-1 text-xl font-semibold">
              {p.customerFirstName ? `Hi ${p.customerFirstName}, ` : ""}
              {formatMoney(p.amountCents)} is due
            </h1>
            <p className="mb-4 text-muted">{p.description}</p>
            {p.refundPolicy ? <Banner tone="info">{p.refundPolicy}</Banner> : null}
            {checkout.error ? <Banner tone="danger">{messageFor(checkout.error)}</Banner> : null}
            {p.payable ? (
              <Button
                className="w-full"
                busy={checkout.isPending}
                onClick={() => checkout.mutate()}
              >
                Pay {formatMoney(p.amountCents)} by card
              </Button>
            ) : (
              <p className="text-muted">
                Card payments are not available right now.
                {call || " Contact the business to pay another way."}
              </p>
            )}
          </>
        )}
      </Card>
      <p className="mt-6 text-center text-xs text-muted">Payments by J360</p>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center p-4">{children}</main>
  );
}
